'use client';

import React, { useEffect, useRef, useState } from 'react';

const allocationData = [
  {
    label: 'Infrastructure & Equipment',
    amount: '$31.2M',
    pct: 93,
    detail: 'Automated housing, climate control, laying systems, processing equipment — European-grade throughout.',
    color: '#FACC15',
  },
  {
    label: 'Pre-Launch Operations',
    amount: '$1.7M',
    pct: 5,
    detail: 'Regulatory approvals, site preparation, initial procurement, team onboarding.',
    color: '#22C55E',
  },
  {
    label: 'Operations Runway',
    amount: '$0.6M',
    pct: 2,
    detail: '6-month working capital reserve ensuring uninterrupted ramp-up to full production.',
    color: '#16A34A',
  },
];

const roadmapItems = [
  { month: 'M1–2', milestone: 'Capital deployment & site finalization', status: 'critical' },
  { month: 'M2–4', milestone: 'Equipment procurement & installation', status: 'critical' },
  { month: 'M4–6', milestone: 'First chick cohort (150K) intake', status: 'key' },
  { month: 'M6–8', milestone: 'Climate systems fully operational', status: 'key' },
  { month: 'M8–10', milestone: 'First egg production cycle begins', status: 'key' },
  { month: 'M10–12', milestone: 'Full 4M egg/year run rate achieved', status: 'launch' },
];

export default function InvestmentSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({ name: '', email: '', fund: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 100);
            });
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -60px 0px' }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // UI-only mock submit
    setSubmitted(true);
  };

  return (
    <section
      id="invest"
      ref={sectionRef}
      className="py-32 lg:py-40"
      style={{ background: 'linear-gradient(180deg, #030D06 0%, #061409 50%, #0A1F0F 100%)' }}
      aria-labelledby="invest-heading"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Label */}
        <div className="scroll-reveal mb-6">
          <span className="section-label">05 / Investment Opportunity</span>
        </div>

        {/* Hero ask */}
        <div className="scroll-reveal mb-20" style={{ transitionDelay: '100ms' }}>
          <div className="text-center max-w-4xl mx-auto">
            <h2 id="invest-heading" className="font-display text-[clamp(2.5rem,6vw,5.5rem)] leading-[0.88] tracking-[-0.04em] text-white mb-6">
              $33.5M to Deploy<br />
              <span className="text-yellow-400">Production Infrastructure.</span>
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
              Capital directly converts into production capacity. Every dollar deployed builds the infrastructure that closes Kenya&apos;s protein supply gap.
            </p>
          </div>
        </div>

        {/* Allocation blocks */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16 stagger-children">
          {allocationData.map((item, idx) => (
            <div
              key={item.label}
              className="scroll-reveal glass-dark rounded-2xl p-8 hover-lift"
              style={{ transitionDelay: `${idx * 120}ms` }}
            >
              {/* Pct ring indicator */}
              <div className="flex items-center justify-between mb-6">
                <div
                  className="text-4xl font-display tracking-[-0.05em]"
                  style={{ color: item.color }}
                >
                  {item.pct}%
                </div>
                <div
                  className="px-3 py-1 rounded-full text-xs font-bold"
                  style={{ background: `${item.color}15`, color: item.color, border: `1px solid ${item.color}25` }}
                >
                  {item.amount}
                </div>
              </div>
              <h3 className="font-display text-lg text-white tracking-tight mb-3">{item.label}</h3>
              <p className="text-sm text-white/90 leading-relaxed">{item.detail}</p>
            </div>
          ))}
        </div>

        {/* Total ask highlighted */}
        <div className="scroll-reveal mb-20" style={{ transitionDelay: '400ms' }}>
          <div
            className="rounded-2xl p-8 lg:p-12 text-center"
            style={{ background: 'linear-gradient(135deg, rgba(250,204,21,0.08) 0%, rgba(22,163,74,0.08) 100%)', border: '1px solid rgba(250,204,21,0.2)' }}
          >
            <p className="section-label mb-3">Total Capital Raise</p>
            <div className="font-display text-[clamp(3rem,8vw,7rem)] text-yellow-400 tracking-[-0.05em] leading-none">
              $33,517,000
            </div>
            <p className="text-white/30 text-lg mt-4 font-medium">Equity · Institutional · Qualified Investors Only</p>
            <div className="flex flex-wrap items-center justify-center gap-8 mt-8">
              {[
                { label: 'Full Operations', value: '12 months' },
                { label: 'First Revenue', value: 'Month 10' },
                { label: 'EBITDA+', value: 'Year 2' },
              ].map((item) => (
                <div key={item.label} className="text-center">
                  <div className="font-display text-2xl text-white tracking-tight">{item.value}</div>
                  <p className="text-xs text-white/30 font-mono-custom uppercase tracking-widest mt-1">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Roadmap */}
        <div className="scroll-reveal mb-20" style={{ transitionDelay: '200ms' }}>
          <h3 className="font-display text-2xl text-white tracking-tight mb-8">
            12-Month Execution Roadmap
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 stagger-children">
            {roadmapItems.map((item, idx) => (
              <div
                key={item.month}
                className="scroll-reveal glass-dark rounded-xl p-5 flex items-start gap-4 hover-lift"
                style={{ transitionDelay: `${idx * 80}ms` }}
              >
                <div
                  className="flex-shrink-0 w-2 h-2 rounded-full mt-2"
                  style={{
                    background: item.status === 'launch' ? '#FACC15' : item.status === 'critical' ? '#EF4444' : '#16A34A',
                    boxShadow: item.status === 'launch' ? '0 0 8px rgba(250,204,21,0.6)' : 'none',
                  }}
                  aria-hidden="true"
                />
                <div>
                  <p className="text-xs text-white/30 font-mono-custom uppercase tracking-widest mb-1">{item.month}</p>
                  <p className="text-sm text-white/70 font-medium leading-snug">{item.milestone}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact form */}
        <div className="scroll-reveal" style={{ transitionDelay: '300ms' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left: context */}
            <div>
              <h3 className="font-display text-3xl text-white tracking-tight mb-4">
                Request Investment<br />Materials
              </h3>
              <p className="text-white/90 leading-relaxed mb-8">
                Qualified investors receive access to the full investment memorandum, financial model, and management presentation. Complete the form to initiate the process.
              </p>
              <div className="space-y-4">
                {[
                  'Full Investment Memorandum (IM)',
                  'Detailed Financial Model (5-Year)',
                  'Management Presentation Deck',
                  'Site & Infrastructure Assessment',
                ].map((doc) => (
                  <div key={doc} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-yellow-accent flex-shrink-0" />
                    <span className="text-sm text-white/50">{doc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: form */}
            <div className="glass-dark rounded-2xl p-8">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-green-primary/20 flex items-center justify-center mx-auto mb-4">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </div>
                  <h4 className="font-display text-xl text-white mb-2">Request Received</h4>
                  <p className="text-white/90 text-sm">Our team will contact you within 24 hours with access credentials to the investment portal.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                  <div>
                    <label htmlFor="inv-name" className="text-xs text-white/90 font-mono-custom uppercase tracking-widest block mb-2">Full Name</label>
                    <input
                      id="inv-name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/50 focus:outline-none focus:border-green-primary/50 transition-colors"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="inv-email" className="text-xs text-white/90 font-mono-custom uppercase tracking-widest block mb-2">Email Address</label>
                    <input
                      id="inv-email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/50 focus:outline-none focus:border-green-primary/50 transition-colors"
                      placeholder="you@fund.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="inv-fund" className="text-xs text-white/90 font-mono-custom uppercase tracking-widest block mb-2">Fund / Organization</label>
                    <input
                      id="inv-fund"
                      type="text"
                      value={formData.fund}
                      onChange={(e) => setFormData({ ...formData, fund: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/50 focus:outline-none focus:border-green-primary/50 transition-colors"
                      placeholder="Institution or fund name"
                    />
                  </div>
                  <div>
                    <label htmlFor="inv-message" className="text-xs text-white/90 font-mono-custom uppercase tracking-widest block mb-2">Message (Optional)</label>
                    <textarea
                      id="inv-message"
                      rows={3}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/50 focus:outline-none focus:border-green-primary/50 transition-colors resize-none"
                      placeholder="Investment thesis or specific questions..."
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn-yellow w-full py-4 rounded-xl text-base font-bold"
                  >
                    Request Investment Materials
                  </button>
                  <p className="text-xs text-white/70 text-center leading-relaxed">
                    For qualified investors only. Information provided is confidential and subject to NDA.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}